/**
 * Zahlungsaufforderungen — Übersicht + Freigabe-Queue + Einstellungen + Ratenzahlungspläne.
 * Nennt intern „Dunning", aber ALLE UI-Texte lauten „Zahlungsaufforderung".
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CurrencyEur, Gear, ClipboardText, CheckCircle, XCircle, Warning, Play, Info,
  Plus, Trash, ArrowsClockwise, HandCoins, MagnifyingGlass, Envelope,
} from "@phosphor-icons/react";
import { formatEUR, formatDate } from "@/lib/constants";

const STAGE_LABELS = {
  1: "Zahlungserinnerung",
  2: "1. Zahlungsaufforderung",
  3: "2. Zahlungsaufforderung",
  4: "Letzte Zahlungsaufforderung",
  deposit: "Kaution — Zahlungsaufforderung",
};
const STAGE_COLORS = {
  1: "bg-sky-100 text-sky-800 border-sky-200",
  2: "bg-amber-100 text-amber-800 border-amber-200",
  3: "bg-orange-100 text-orange-800 border-orange-200",
  4: "bg-rose-100 text-rose-800 border-rose-200",
  deposit: "bg-purple-100 text-purple-800 border-purple-200",
};
const SCENARIO_SHORT = {
  full_unpaid: "voller Rückstand",
  partial: "Teilzahlung",
  rent_only_paid: "nur Kaltmiete gezahlt",
  nk_only_paid: "nur NK gezahlt",
  bka_open: "BKA-Nachzahlung",
  old_arrears_only: "Altrückstände",
  multi_period_open: "mehrere Perioden offen",
  plan_active: "aktiver Ratenplan",
  plan_broken: "Ratenplan gebrochen",
  other_open: "sonstige Forderung",
  cash_missing_full: "Kaution komplett offen",
  cash_missing_partial: "Kaution teilweise offen",
  cash_installment_overdue: "Kautionsrate überfällig",
  buergschaft_missing: "Bürgschaftsurkunde fehlt",
  verpfaendung_missing: "Verpfändung offen",
};
const PLAN_HEALTH = {
  on_track:  { label: "planmäßig", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  overdue:   { label: "überfällig", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  completed: { label: "abgeschlossen", cls: "bg-slate-200 text-slate-700 border-slate-300" },
  unknown:   { label: "unbekannt", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function Zahlungsaufforderungen() {
  const [preview, setPreview] = useState(null);
  const [pending, setPending] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [modes, setModes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [plans, setPlans] = useState([]);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [previewRec, setPreviewRec] = useState(null);       // Vorschau-Dialog

  const load = async () => {
    const [{ data: s }, { data: p }, { data: pr }, { data: pl }] = await Promise.all([
      api.get("/dunning/settings"),
      api.get("/dunning/pending"),
      api.post("/dunning/preview"),
      api.get("/dunning/payment-plans"),
    ]);
    setCfg(s.config);
    setModes(s.modes || []);
    setPending(p || []);
    setPreview(pr);
    setPlans(pl || []);
  };
  useEffect(() => { load(); }, []);

  const setCfgField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const saveCfg = async () => {
    setBusy(true);
    try {
      await api.patch("/dunning/settings", cfg);
      toast.success("Einstellungen gespeichert");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    if (!await window._domivusConfirm("Zahlungsaufforderungs-Lauf jetzt starten?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/dunning/run-now");
      toast.success(`${data.created} neue Vorschläge zur Freigabe`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const approve = async (rec) => {
    // 2026-02: statt sofortigem Versand zuerst PDF-Vorschau anzeigen.
    setPreviewRec(rec);
  };
  const approveConfirmed = async (rec) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dunning/pending/${rec.id}/approve`, {});
      toast.success(`Freigegeben & versendet (${(data.result?.channels || []).join(", ") || "gespeichert"})`);
      setPreviewRec(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Versand");
    } finally { setBusy(false); }
  };
  const reject = async (rec) => {
    const note = window.prompt(`Warum wird die ${STAGE_LABELS[rec.level]} für ${rec.contract_number} verworfen?`, "");
    if (note === null) return;
    await api.post(`/dunning/pending/${rec.id}/reject`, { note });
    toast.info("Verworfen");
    load();
  };

  const toggleSelected = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === pending.length) setSelected(new Set());
    else setSelected(new Set(pending.map((p) => p.id)));
  };
  const bulkApprove = async () => {
    if (selected.size === 0) return;
    if (!await window._domivusConfirm(`${selected.size} Zahlungsaufforderung(en) freigeben und versenden?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/dunning/pending/bulk-approve", {
        pending_ids: Array.from(selected),
      });
      const msg = `${data.approved} freigegeben` + (data.failed?.length ? `, ${data.failed.length} fehlgeschlagen` : "");
      data.failed?.length ? toast.warning(msg) : toast.success(msg);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-Freigabe fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const allSelected = pending.length > 0 && selected.size === pending.length;

  if (!cfg) return <div className="p-6 text-slate-500">Lade …</div>;

  return (
    <div className="space-y-4" data-testid="zahlungsaufforderungen-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <CurrencyEur size={24} weight="bold" /> Zahlungsaufforderungen
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Automatischer Mahnlauf mit 4 Stufen und Freigabe-Queue. Aktuell im Modus{" "}
            <strong>{modes.find((m) => m.key === cfg.mode)?.label || cfg.mode}</strong>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/briefvorlagen/zahlungsaufforderungen">
            <Button variant="outline" className="gap-1.5" data-testid="link-briefvorlagen">
              <Envelope size={14} /> Briefvorlagen
            </Button>
          </Link>
          <Button onClick={runNow} disabled={busy || !cfg.enabled} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="dunning-run-now">
            <Play size={14} /> Lauf jetzt starten
          </Button>
        </div>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="dunning-tab-pending">
            Freigabe-Queue ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="preview" data-testid="dunning-tab-preview">
            Vorschau heutiger Lauf ({preview?.count ?? 0})
          </TabsTrigger>
          <TabsTrigger value="plans" data-testid="dunning-tab-plans">
            <HandCoins size={14} className="mr-1.5" /> Ratenzahlung ({plans.length})
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="dunning-tab-settings">
            <Gear size={14} className="mr-1.5" /> Einstellungen
          </TabsTrigger>
        </TabsList>

        {/* --- FREIGABE-QUEUE (Semi-Modus) --- */}
        <TabsContent value="pending" className="pt-3">
          {selected.size > 0 && (
            <div className="mb-3 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center justify-between gap-3">
              <span className="text-sm text-emerald-900">
                <strong>{selected.size}</strong> markiert. Bulk-Freigabe erzeugt PDFs, verbucht Mahngebühren und versendet sofort.
              </span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Auswahl aufheben</Button>
                <Button size="sm" onClick={bulkApprove} disabled={busy}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5" data-testid="bulk-approve">
                  <CheckCircle size={14} /> Alle markierten freigeben
                </Button>
              </div>
            </div>
          )}
          {pending.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center text-slate-500">
              <ClipboardText size={32} className="mx-auto mb-2 text-slate-300" />
              Keine offenen Vorschläge zur Freigabe.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-3 py-2 w-10">
                      <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="bulk-toggle-all" />
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Vertrag</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Stufe · Szenario</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Rückstand</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Mahngeb.</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Gesamt</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Frist</th>
                    <th className="w-40"></th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((rec) => (
                    <tr key={rec.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pending-row-${rec.id}`}>
                      <td className="px-3 py-2">
                        <Checkbox checked={selected.has(rec.id)} onCheckedChange={() => toggleSelected(rec.id)} data-testid={`bulk-cb-${rec.id}`} />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        <Link to={`/mietverhaeltnisse/${rec.tenancy_id}`} className="text-slate-800 hover:underline">
                          {rec.contract_number}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STAGE_COLORS[rec.level] || ""}`}>
                          {STAGE_LABELS[rec.level] || `Stufe ${rec.level}`}
                        </span>
                        {rec.scenario && SCENARIO_SHORT[rec.scenario] && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200"
                                title={rec.scenario_label}>
                            {SCENARIO_SHORT[rec.scenario]}
                          </span>
                        )}
                        {rec.terminate_warning && (
                          <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full bg-rose-200 text-rose-900" title="Kündigungsandrohung §543 BGB">
                            <Warning size={9} weight="fill" className="inline mr-0.5" /> Kündigung
                          </span>
                        )}
                        {rec.letzte_faelligkeit && (
                          <div className="text-[10px] text-slate-500 mt-0.5">letzte Fälligkeit: {formatDate(rec.letzte_faelligkeit)}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatEUR(rec.outstanding_eur)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatEUR(rec.fee_eur)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatEUR(rec.total_eur)}</td>
                      <td className="px-3 py-2 text-xs">{formatDate(rec.due_date)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" className="text-slate-800 hover:bg-slate-100" onClick={() => approve(rec)} data-testid={`preview-${rec.id}`}>
                          <MagnifyingGlass size={13} className="mr-1" /> Vorschau &amp; Freigeben
                        </Button>
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => reject(rec)} data-testid={`reject-${rec.id}`}>
                          <XCircle size={13} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* --- PREVIEW --- */}
        <TabsContent value="preview" className="pt-3">
          <div className="mb-3 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-xs text-blue-900 flex items-start gap-2">
            <Info size={14} className="mt-0.5 flex-shrink-0" />
            Zeigt, welche Zahlungsaufforderungen bei einem Lauf heute erstellt würden. Nichts wird versendet, bis Sie im Semi-Modus freigeben oder im Auto-Modus der Nachtjob läuft.
          </div>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Vertrag</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Nächste Stufe</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Rückstand</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Mahngeb.</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {(preview?.candidates || []).length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-slate-500 text-sm">Keine Kandidaten.</td></tr>
                )}
                {(preview?.candidates || []).map((c, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/mietverhaeltnisse/${c.tenancy_id}`} className="text-slate-800 hover:underline">
                        {c.contract_number}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STAGE_COLORS[c.next_level] || ""}`}>
                        {STAGE_LABELS[c.next_level] || `Stufe ${c.next_level}`}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatEUR(c.outstanding_eur)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatEUR(c.fee_eur)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatEUR(c.total_eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* --- Payment Plans --- */}
        <TabsContent value="plans" className="pt-3">
          <PaymentPlansPanel plans={plans} onReload={load} onOpenDialog={() => setPlanDialogOpen(true)} />
        </TabsContent>

        {/* --- SETTINGS --- */}
        <TabsContent value="settings" className="pt-3">
          <div className="bg-white border border-slate-200 rounded-md p-5 space-y-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="text-sm">Zahlungsaufforderungen aktiviert</Label>
                <p className="text-xs text-slate-500">Nur wenn aktiviert, laufen Nachtjob & manuelle Auslösung.</p>
              </div>
              <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfgField("enabled", v)} data-testid="dunning-enabled" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">Betriebsmodus</Label>
              <Select value={cfg.mode} onValueChange={(v) => setCfgField("mode", v)}>
                <SelectTrigger data-testid="dunning-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {modes.map((m) => (
                    <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">{modes.find((m) => m.key === cfg.mode)?.description}</p>
            </div>

            <div className="grid grid-cols-4 gap-3 pt-3 border-t border-slate-200">
              <div className="col-span-4 text-xs font-semibold uppercase tracking-wider text-slate-600">
                Stufen &amp; Fristen (Tage) + Mahngebühr (€)
              </div>
              {[1, 2, 3, 4].map((lvl) => (
                <div key={lvl} className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50/40">
                  <div className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full inline-block border ${STAGE_COLORS[lvl]}`}>
                    {STAGE_LABELS[lvl]}
                  </div>
                  <div>
                    <Label className="text-[10px]">Frist ab {lvl === 1 ? "Fälligkeit" : `Stufe ${lvl - 1}`}</Label>
                    <Input
                      type="number" min="0" max="365"
                      value={cfg[`grace_days_level${lvl}`] ?? 0}
                      onChange={(e) => setCfgField(`grace_days_level${lvl}`, Number(e.target.value))}
                      data-testid={`grace-l${lvl}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">Mahngebühr</Label>
                    <Input
                      type="number" step="0.01" min="0" max="999"
                      value={cfg[`fee_level${lvl}`] ?? 0}
                      onChange={(e) => setCfgField(`fee_level${lvl}`, Number(e.target.value))}
                      data-testid={`fee-l${lvl}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-200">
              <div>
                <Label className="text-xs">Mindestbetrag für ZA (€)</Label>
                <Input type="number" step="0.01" min="0" value={cfg.min_open_amount ?? 1}
                  onChange={(e) => setCfgField("min_open_amount", Number(e.target.value))} data-testid="min-amount" />
              </div>
              <div>
                <Label className="text-xs">Versandart</Label>
                <Select value={cfg.delivery || "post"} onValueChange={(v) => setCfgField("delivery", v)}>
                  <SelectTrigger data-testid="delivery"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="post">Postversand</SelectItem>
                    <SelectItem value="email">E-Mail</SelectItem>
                    <SelectItem value="both">Beides</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Kündigungs-Schwelle (Monatsmieten)</Label>
                <Input type="number" step="0.5" min="0" max="12" value={cfg.terminate_threshold_months ?? 2}
                  onChange={(e) => setCfgField("terminate_threshold_months", Number(e.target.value))} data-testid="terminate-months" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-200 items-end">
              <div className="col-span-3 text-xs font-semibold uppercase tracking-wider text-slate-600">
                Kaution-Zahlungsaufforderung
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!cfg.deposit_dunning_enabled} onCheckedChange={(v) => setCfgField("deposit_dunning_enabled", v)} data-testid="deposit-dunning-enabled" />
                <span className="text-xs">Aktiviert</span>
              </div>
              <div>
                <Label className="text-xs">Karenztage nach Vertragsbeginn</Label>
                <Input type="number" min="0" max="365" value={cfg.deposit_grace_days ?? 14}
                  onChange={(e) => setCfgField("deposit_grace_days", Number(e.target.value))} data-testid="deposit-grace" />
              </div>
            </div>

            <div className="flex justify-end pt-3 border-t border-slate-200">
              <Button onClick={saveCfg} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="dunning-save">
                {busy ? "Speichere…" : "Einstellungen speichern"}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <PaymentPlanDialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} onSaved={load} />
      <PreviewDialog
        rec={previewRec}
        onClose={() => setPreviewRec(null)}
        onApprove={() => approveConfirmed(previewRec)}
        busy={busy}
      />
    </div>
  );
}


// ---------------- PDF-Vorschau vor Freigabe ----------------

function PreviewDialog({ rec, onClose, onApprove, busy }) {
  const [meta, setMeta] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    if (!rec) {
      setMeta(null); setPdfUrl(null);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const { data: m } = await api.get(`/dunning/pending/${rec.id}/preview`);
        if (cancelled) return;
        setMeta(m);
        const res = await api.get(`/dunning/pending/${rec.id}/preview.pdf`, { responseType: "blob" });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setPdfUrl(objectUrl);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [rec]);

  if (!rec) return null;
  return (
    <Dialog open={!!rec} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MagnifyingGlass size={18} /> Vorschau — {STAGE_LABELS[rec.level] || `Stufe ${rec.level}`} · {rec.contract_number}
          </DialogTitle>
          <DialogDescription>
            Prüfen Sie die fertige PDF, bevor Sie die Zahlungsaufforderung freigeben und versenden.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-4 space-y-2 text-sm">
            {meta && (
              <>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1.5 text-xs">
                  <div><span className="text-slate-500">Vorlage:</span><br /><strong>{meta.template?.name || "— (Fallback Systemvorlage)"}</strong></div>
                  <div><span className="text-slate-500">Situation:</span> {SCENARIO_SHORT[meta.scenario] || meta.scenario}</div>
                  <div><span className="text-slate-500">Rückstand:</span> <strong>{formatEUR(meta.outstanding_eur)}</strong></div>
                  <div><span className="text-slate-500">Mahngebühr:</span> {formatEUR(meta.fee_eur)}</div>
                  <div className="border-t border-slate-300 pt-1.5 mt-1.5"><span className="text-slate-500">Gesamt:</span> <strong className="text-lg">{formatEUR(meta.total_eur)}</strong></div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2 text-[11px] text-emerald-900">
                  <Envelope size={11} className="inline mr-1" />
                  E-Mail an: {(meta.email_recipients || []).join(", ") || "—"}
                </div>
                {meta.scenario_label && (
                  <div className="text-[11px] text-slate-500 italic">{meta.scenario_label}</div>
                )}
              </>
            )}
          </div>
          <div className="col-span-8">
            {loading && (
              <div className="h-[70vh] flex items-center justify-center bg-slate-50 border border-slate-200 rounded-md text-slate-500">
                Vorschau wird erstellt …
              </div>
            )}
            {!loading && pdfUrl && (
              <iframe
                src={pdfUrl}
                title="PDF-Vorschau"
                className="w-full h-[70vh] border border-slate-200 rounded-md bg-white"
                data-testid="preview-iframe"
              />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="preview-cancel">Verwerfen / Schließen</Button>
          <Button
            onClick={onApprove}
            disabled={busy || loading}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="preview-approve"
          >
            <CheckCircle size={13} className="mr-1" /> Freigeben &amp; Versenden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Payment Plans Panel ----------------

function PaymentPlansPanel({ plans, onReload, onOpenDialog }) {
  const recheck = async (id) => {
    try {
      await api.post(`/dunning/payment-plans/${id}/recheck`);
      toast.success("Plan neu geprüft");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };
  const remove = async (id) => {
    if (!await window._domivusConfirm("Ratenzahlungsvereinbarung wirklich löschen? Etwaiger Mahnstopp wird aufgehoben.")) return;
    await api.delete(`/dunning/payment-plans/${id}`);
    toast.info("Gelöscht");
    onReload();
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-slate-500">
          Ratenzahlungspläne mit automatischer Bank-Zuordnung. Solange Raten planmäßig eingehen,
          wird der Zahlungsaufforderungs-Lauf für dieses Mietverhältnis pausiert.
        </div>
        <Button onClick={onOpenDialog} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="plan-new">
          <Plus size={14} /> Neue Vereinbarung
        </Button>
      </div>
      {plans.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center text-slate-500">
          <HandCoins size={32} className="mx-auto mb-2 text-slate-300" />
          Keine Ratenzahlungsvereinbarungen aktiv.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Vertrag</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Start</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Rate/Monat</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Fortschritt</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Bezahlt</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Status</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`plan-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{p.tenancy_id.slice(0, 8)}…</td>
                  <td className="px-3 py-2 text-xs">{formatDate(p.start_date)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatEUR(p.monthly_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {p.health?.installments_paid ?? 0} / {p.installments}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatEUR(p.health?.paid_amount || 0)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${PLAN_HEALTH[p.health?.status || "unknown"].cls}`}>
                      {PLAN_HEALTH[p.health?.status || "unknown"].label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => recheck(p.id)} data-testid={`plan-recheck-${p.id}`}>
                      <ArrowsClockwise size={13} />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(p.id)} data-testid={`plan-delete-${p.id}`}>
                      <Trash size={13} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PaymentPlanDialog({ open, onClose, onSaved }) {
  const [tenancies, setTenancies] = useState([]);
  const [form, setForm] = useState({
    tenancy_id: "", start_date: new Date().toISOString().slice(0, 10),
    monthly_amount: 100, installments: 6, reason: "", notes: "",
  });
  useEffect(() => {
    if (open) {
      api.get("/tenancies?status=active&limit=500")
        .then(({ data }) => setTenancies(data.items || data || []))
        .catch(() => setTenancies([]));
    }
  }, [open]);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.tenancy_id) return toast.error("Mietverhältnis auswählen");
    try {
      await api.post("/dunning/payment-plans", {
        tenancy_id: form.tenancy_id,
        start_date: form.start_date,
        monthly_amount: Number(form.monthly_amount),
        installments: Number(form.installments),
        reason: form.reason || null,
        notes: form.notes || null,
      });
      toast.success("Ratenzahlung angelegt");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="plan-dialog">
        <DialogHeader><DialogTitle>Neue Ratenzahlungsvereinbarung</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Mietverhältnis *</Label>
            <Select value={form.tenancy_id} onValueChange={(v) => setField("tenancy_id", v)}>
              <SelectTrigger data-testid="plan-tenancy"><SelectValue placeholder="MV wählen …" /></SelectTrigger>
              <SelectContent>
                {tenancies.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.contract_number}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Startdatum *</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setField("start_date", e.target.value)} data-testid="plan-start" />
            </div>
            <div>
              <Label className="text-xs">Rate/Monat (€) *</Label>
              <Input type="number" step="0.01" min="1" value={form.monthly_amount}
                     onChange={(e) => setField("monthly_amount", e.target.value)} data-testid="plan-amount" />
            </div>
            <div>
              <Label className="text-xs">Anzahl Raten *</Label>
              <Input type="number" min="1" max="60" value={form.installments}
                     onChange={(e) => setField("installments", e.target.value)} data-testid="plan-installments" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Grund (optional)</Label>
            <Input value={form.reason} onChange={(e) => setField("reason", e.target.value)}
                   placeholder="z. B. Nebenkosten-Nachzahlung" data-testid="plan-reason" />
          </div>
          <div>
            <Label className="text-xs">Notizen (optional)</Label>
            <Input value={form.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="plan-notes" />
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            <Info size={12} className="inline mr-1" />
            Bank-Zuordnung: Eingehende Zahlungen auf das Mietkonto werden automatisch gegen die Raten geprüft.
            Solange planmäßig gezahlt wird, pausiert der ZA-Lauf.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="plan-save">Anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
